import React, { useEffect, useState, type MouseEvent } from 'react'
import QRCodeGenerator from 'qrcode'
import { OpenInNew } from '@mui/icons-material'
import { formatGrooverText } from '../../libs/GrooverTextRepository'
import { DropinTicket } from '../../../../pure-js/types/GrooverTypes'
import { formatDate, getStatusLabel, isExpiringWithin30Days } from './portalDropinTicketHelpers'
import { QrExpandedDialog } from '../../components/qr/QrExpandedDialog'
import {
  CardHeroRow,
  CardTopRow,
  ExpiringSoonBadge,
  PassesBig,
  PassesHeroColumn,
  PassesHeroRow,
  PassesLeftLabel,
  ProgressBar,
  ProgressFill,
  QrThumbButton,
  QrThumbColumn,
  QrThumbInner,
  QrThumbPlaceholder,
  SchoolSubtitle,
  StatusPill,
  TapForQrButton,
  TicketCard,
  TicketTitle,
  ValidUntilFooter
} from './PortalDropinTicketsPage.styles'

export type PortalDropinGetText = (key: string) => string

interface PassesHeroProps {
  ticket: DropinTicket
  getText: PortalDropinGetText
}

/** Shared with detail modal (same pass count + progress UI). */
export const PortalDropinTicketPassesHero: React.FC<PassesHeroProps> = ({ ticket, getText }) => {
  const ticketsRemaining = ticket.ticketCount - ticket.ticketsUsed
  const pct = ticket.ticketCount > 0 ? (ticketsRemaining / ticket.ticketCount) * 100 : 0
  return (
    <PassesHeroColumn>
      <PassesHeroRow>
        <PassesBig>{ticketsRemaining}</PassesBig>
        <PassesLeftLabel>
          {formatGrooverText(getText('portalDropinTicketPassesLeft'), [
            String(ticketsRemaining),
            String(ticket.ticketCount)
          ])}
        </PassesLeftLabel>
      </PassesHeroRow>
      <ProgressBar aria-hidden>
        <ProgressFill style={{ width: `${pct}%` }} />
      </ProgressBar>
    </PassesHeroColumn>
  )
}

interface TicketCardComponentProps {
  ticket: DropinTicket
  getText: PortalDropinGetText
  onClick: () => void
}

export const PortalDropinTicketCard: React.FC<TicketCardComponentProps> = ({ ticket, getText, onClick }) => {
  const expiringSoon = isExpiringWithin30Days(ticket.validUntil)
  const qrCodeValue = `d/${ticket.id}`
  const [pngDataUrl, setPngDataUrl] = useState<string | null>(null)
  const [showQrExpanded, setShowQrExpanded] = useState(false)

  const openQr = (e: MouseEvent) => {
    e.stopPropagation()
    setShowQrExpanded(true)
  }

  useEffect(() => {
    let cancelled = false
    QRCodeGenerator.toDataURL(qrCodeValue, {
      errorCorrectionLevel: 'H',
      margin: 0,
      width: 84,
      color: { dark: '#000000', light: '#FFFFFF' }
    })
      .then((url) => {
        if (!cancelled) setPngDataUrl(url)
      })
      .catch(() => {
        if (!cancelled) setPngDataUrl(null)
      })
    return () => {
      cancelled = true
    }
  }, [qrCodeValue])

  return (
    <TicketCard role="button" tabIndex={0} onClick={onClick} onKeyDown={(e) => e.key === 'Enter' && onClick()}>
      <CardTopRow>
        <StatusPill status={ticket.status}>{getStatusLabel(ticket.status, getText)}</StatusPill>
        {expiringSoon && <ExpiringSoonBadge>{getText('portalDropinTicketExpiringSoon')}</ExpiringSoonBadge>}
      </CardTopRow>
      <TicketTitle>{ticket.packageName || getText('portalDropinTicketPackage')}</TicketTitle>
      <SchoolSubtitle>{ticket.schoolName}</SchoolSubtitle>
      <CardHeroRow>
        <PortalDropinTicketPassesHero ticket={ticket} getText={getText} />
        <QrThumbColumn>
          <QrThumbButton type="button" onClick={openQr} aria-label={getText('portalDropinTicketTapToEnlarge')}>
            <QrThumbInner>
              {/* Placeholder while the PNG is generated — the SVG it replaced rendered on first
                  paint, so without it every card in the list reflows when the data URL lands. */}
              {pngDataUrl ? <img src={pngDataUrl} alt="" width={84} height={84} /> : <QrThumbPlaceholder aria-hidden />}
            </QrThumbInner>
          </QrThumbButton>
          <TapForQrButton type="button" onClick={openQr}>
            <OpenInNew style={{ fontSize: 14 }} aria-hidden />
            {getText('portalDropinTicketTapForQr')}
          </TapForQrButton>
        </QrThumbColumn>
      </CardHeroRow>
      <ValidUntilFooter>
        {formatGrooverText(getText('portalDropinTicketValidUntilShort'), [formatDate(ticket.validUntil)])}
      </ValidUntilFooter>
      <QrExpandedDialog
        open={showQrExpanded}
        value={qrCodeValue}
        onClose={() => setShowQrExpanded(false)}
        closeLabel={getText('portalDropinTicketClose')}
        instruction={getText('portalDropinTicketShowQrAtStudio')}
        labelledBy={`portal-dropin-qr-${ticket.id}`}
      />
    </TicketCard>
  )
}
